import React from 'react';
import Grid2 from '@mui/material/Unstable_Grid2';
import { Card, CardContent, Typography, Box, Stack, Divider } from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoIcon from '@mui/icons-material/Info';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FlightIcon from '@mui/icons-material/Flight';

const SummaryCards = ({ data, onCardClick }) => {
  if (!data) {
    return (
      <Grid2 container>
        <Grid2 xs={12}>
          <Typography
            variant="body2"
            sx={{ textAlign: 'center', color: 'text.secondary', py: 3 }}
          >
            No summary data available
          </Typography>
        </Grid2>
      </Grid2>
    );
  }

  const riskCards = [
    {
      id: 'high_risk',
      label: 'High Risk',
      Icon: ErrorIcon,
      muiColor: 'error.main',
      subtitle: 'Immediate Action',
      classification: 'HIGH_RISK',
      count: data.high_risk_count || 0,
    },
    {
      id: 'medium_risk',
      label: 'Medium Risk',
      Icon: WarningAmberIcon,
      muiColor: 'warning.main',
      subtitle: 'Review Soon',
      classification: 'MEDIUM_RISK',
      count: data.medium_risk_count || 0,
    },
    {
      id: 'low_risk',
      label: 'Low Risk',
      Icon: InfoIcon,
      muiColor: 'info.main',
      subtitle: 'Monitor',
      classification: 'LOW_RISK',
      count: data.low_risk_count || 0,
    },
    {
      id: 'no_threat',
      label: 'No Threat',
      Icon: CheckCircleIcon,
      muiColor: 'success.main',
      subtitle: 'Normal Activity',
      classification: 'NO_THREAT',
      count: data.no_threat_count || 0,
    },
    {
      id: 'impossible_travel',
      label: 'Impossible Travel',
      Icon: FlightIcon,
      muiColor: 'secondary.main',
      subtitle: 'Location Anomaly',
      classification: null,
      count: data.impossible_travel_count || 0,
    },
  ];

  const handleCardClick = (card) => {
    if (onCardClick && card.classification) {
      onCardClick({ classification: card.classification });
    }
  };

  return (
    <>
      <Grid2 container spacing={2}>
        {riskCards.map((card) => {
          const { Icon } = card;

          return (
            <Grid2 key={card.id} xs={12} sm={6} lg>
              <Card
                sx={{
                  height: '100%',
                  cursor: card.classification ? 'pointer' : 'default',
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'all 0.2s',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 3,
                    bgcolor: card.muiColor,
                  },
                  ...(card.classification && {
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: 4,
                    },
                  }),
                }}
                onClick={() => handleCardClick(card)}
              >
                <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                      <Typography
                        variant="overline"
                        sx={{
                          color: 'text.secondary',
                          fontSize: '0.68rem',
                          fontWeight: 600,
                          letterSpacing: '0.8px',
                          display: 'block',
                          mb: 0.5,
                        }}
                      >
                        {card.label}
                      </Typography>
                      <Typography
                        variant="h4"
                        sx={{ color: card.muiColor, fontWeight: 700, lineHeight: 1.2 }}
                      >
                        {card.count}
                      </Typography>
                    </Box>
                    <Box sx={{ p: 1, borderRadius: 2, bgcolor: 'background.subtle' }}>
                      <Icon sx={{ color: card.muiColor, fontSize: 22 }} />
                    </Box>
                  </Stack>

                  <Divider sx={{ my: 1 }} />

                  <Typography variant="caption" color="text.secondary">
                    {card.subtitle}
                  </Typography>
                </CardContent>
              </Card>
            </Grid2>
          );
        })}
      </Grid2>

      {/* Summary Stats Row */}
      <Grid2 container spacing={2} sx={{ mt: 2 }}>
        <Grid2 xs={12} md={4}>
          <Box
            sx={{
              textAlign: 'center',
              p: 1,
              bgcolor: 'background.paper',
              borderRadius: 1,
            }}
          >
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Total Analyzed
            </Typography>
            <Typography variant="h6" sx={{ mt: 0.5 }}>
              {data.total_users_analyzed || 0}
            </Typography>
          </Box>
        </Grid2>
        <Grid2 xs={12} md={4}>
          <Box
            sx={{
              textAlign: 'center',
              p: 1,
              bgcolor: 'background.paper',
              borderRadius: 1,
            }}
          >
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Avg Suspicion Score
            </Typography>
            <Typography variant="h6" sx={{ mt: 0.5 }}>
              {data.average_suspicion_score
                ? data.average_suspicion_score.toFixed(1)
                : '0.0'}
              /100
            </Typography>
          </Box>
        </Grid2>
        <Grid2 xs={12} md={4}>
          <Box
            sx={{
              textAlign: 'center',
              p: 1,
              bgcolor: 'background.paper',
              borderRadius: 1,
            }}
          >
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Avg Confidence
            </Typography>
            <Typography variant="h6" sx={{ mt: 0.5 }}>
              {data.average_confidence_score
                ? (data.average_confidence_score * 100).toFixed(0)
                : '0'}
              %
            </Typography>
          </Box>
        </Grid2>
      </Grid2>
    </>
  );
};

export default SummaryCards;
